/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-len */
/* eslint-disable no-underscore-dangle */

'use-strict';

import { IScope } from 'angular';
import { NumberConversionService } from '../../../shared/converter/number-converter.service';
import { ConfigService } from '../../../shared/config/config.service';
import { AutoboardVehicle } from '../../../src/app/@shared-types/vehicle';
import LogService from '../../../src/app/shared/services/log';
import { VehicleFormComponentExitFunction } from '../../../src/app/@types/vehicle';
import { EOfferType } from '../../../src/app/@shared-types/opportunites/enums';
import { Offer, OfferData, OfferStub } from '../../../src/app/@shared-types/opportunites/offer';
import OfferModel from '../../../src/app/shared/models/offer/offer';
import { ToastService, ToastType } from '../../../ui-components/toast/toast.service';
import { FileService } from '../../../shared/storage/file.service';
import { EStaticFileType, StaticUploadRequest } from '../../../src/app/@shared-types/misc/file';
import { EConstantType } from '../../../src/app/@shared-types/config/enums';
import { SalesAPIService } from '../../../shared/api/sales-api.service';
import OfferFormService from './offer-form.helper';
import { OfferFormData } from '../../../src/app/@types/offer';

interface OfferScope extends IScope {
  vehicle: AutoboardVehicle,
  customerId?: string,
  exit: (data: VehicleFormComponentExitFunction) => void,
  dummyVehicle: boolean,
}

/* @ngInject */
export default class OfferFormController {
  public sending = false;

  public analyzing = false;

  private exit: (data: VehicleFormComponentExitFunction) => void;

  private vehicle!: AutoboardVehicle;

  private $scope: OfferScope;

  private numberConversionService: NumberConversionService;

  private logService: LogService;

  private offerModel: OfferModel;

  private toastService: ToastService;

  private fileService: FileService;

  private salesApiService: SalesAPIService;

  private offerFormService: OfferFormService;

  public bankInformation: { [key: string]: string };

  public offerData: OfferFormData;

  constructor(
    $scope: OfferScope,
    numberConversionService: NumberConversionService,
    configService: ConfigService,
    logService: LogService,
    offerModel: OfferModel,
    toastService: ToastService,
    fileService: FileService,
    salesApiService: SalesAPIService,
    offerFormService: OfferFormService,
  ) {
    this.numberConversionService = numberConversionService;
    this.logService = logService;
    this.$scope = $scope;
    this.offerModel = offerModel;
    this.toastService = toastService;
    this.fileService = fileService;
    this.salesApiService = salesApiService;
    this.offerFormService = offerFormService;

    this.vehicle = this.$scope.vehicle;
    this.exit = this.$scope.exit;

    this.bankInformation = configService.get(EConstantType.BANK_INFORMATION) || {};

    this.offerData = {
      type: EOfferType.CASH,
      customerId: this.$scope.customerId,
      customer: null,
      generalData: {
        extras: [],
        includesVat: false,
      },
      leasingData: {
        optionToBuy: false,
      },
      financingData: {},
      cashData: {},
      residualData: {
        optionToBuy: false,
      },
      offerFile: null,
    };

    this.initializeForm();
  }

  /** Pre-populate data from vehicle if present. */
  private initializeForm = (): void => {
    const {
      retailPrices,
      transferCosts,
      includesTransferCosts,
      includesVat,
      campaignPrice,
    } = this.vehicle.vehicleData.price;
    const { generalData } = this.offerData;

    // RRP
    if (retailPrices.manufacturer) generalData.rrp = retailPrices.manufacturer;
    if (retailPrices.invoice) generalData.rrp = retailPrices.invoice;
    if (retailPrices.offer) generalData.rrp = retailPrices.offer;

    // Extras
    if (includesTransferCosts && transferCosts) generalData.extras?.push({ description: 'Überführungskosten', price: transferCosts });

    // Offer price
    if (campaignPrice) generalData.offerPrice = campaignPrice;

    // VAT
    if (includesVat) {
      generalData.includesVat = true;

      /** Calculate VAT for all fields, that do exclude VAT */
      this.calculateVatFields(true);
    }

    this.calculateDiscount();
  };

  public calculateVatFields = (
    skipVatFields?: boolean,
    skipOfferFields?: boolean,
  ): void => {
    const {
      generalData,
      leasingData,
      financingData,
      residualData,
    } = this.offerData;
    const { vatRate } = this.vehicle.vehicleData.price;
    const includesVat = generalData.includesVat || false;

    if (generalData.rrp) generalData.rrp = this.numberConversionService.calculateVat(generalData.rrp, includesVat, vatRate);
    if (generalData.extras) {
      generalData.extras.forEach((e: any) => {
        e.price = this.numberConversionService.calculateVat(e.price, includesVat, vatRate);
      });
    }

    /** We skip updating VAT on financin data if the data provided
     * came from AI recognition and already had the correct VAT calculation. */
    if (!skipOfferFields) {
      if (leasingData.chargeMileage) leasingData.chargeMileage = this.numberConversionService.calculateVat(leasingData.chargeMileage, includesVat, vatRate);
      if (leasingData.firstPayment) leasingData.firstPayment = this.numberConversionService.calculateVat(leasingData.firstPayment, includesVat, vatRate);
      if (leasingData.monthlyRate) leasingData.monthlyRate = this.numberConversionService.calculateVat(leasingData.monthlyRate, includesVat, vatRate);
      if (leasingData.refundMileage) leasingData.refundMileage = this.numberConversionService.calculateVat(leasingData.refundMileage, includesVat, vatRate);

      if (financingData.monthlyRate) financingData.monthlyRate = this.numberConversionService.calculateVat(financingData.monthlyRate, includesVat, vatRate);
      if (financingData.firstPayment) financingData.firstPayment = this.numberConversionService.calculateVat(financingData.firstPayment, includesVat, vatRate);
      if (financingData.firstRate) financingData.firstRate = this.numberConversionService.calculateVat(financingData.firstRate, includesVat, vatRate);
      if (financingData.lastRate) financingData.lastRate = this.numberConversionService.calculateVat(financingData.lastRate, includesVat, vatRate);
      if (financingData.loanTotal) financingData.loanTotal = this.numberConversionService.calculateVat(financingData.loanTotal, includesVat, vatRate);
      if (financingData.sumTotal) financingData.sumTotal = this.numberConversionService.calculateVat(financingData.sumTotal, includesVat, vatRate);

      if (residualData.firstPayment) residualData.firstPayment = this.numberConversionService.calculateVat(residualData.firstPayment, includesVat, vatRate);
      if (residualData.monthlyRate) residualData.monthlyRate = this.numberConversionService.calculateVat(residualData.monthlyRate, includesVat, vatRate);
      if (residualData.residualValue) residualData.residualValue = this.numberConversionService.calculateVat(residualData.residualValue, includesVat, vatRate);
      if (residualData.sumTotal) residualData.sumTotal = this.numberConversionService.calculateVat(residualData.sumTotal, includesVat, vatRate);
    }

    /** For an additional calculation we need to skip the values,
     * that already invluce VAT, because that is how they are saved
     * in `AutoboardVehicle`. */
    if (skipVatFields) return;
    if (generalData.offerPrice) generalData.offerPrice = this.numberConversionService.calculateVat(generalData.offerPrice, includesVat, vatRate);

    this.calculateDiscount();
  };

  /** Discount calculation for cash offer.
   * Discount is the difference from rrp and offer price plus
   * sum of extras. */
  public calculateDiscount = (): void => {
    const { extras, rrp, offerPrice } = this.offerData.generalData;

    if (!rrp || !offerPrice) {
      this.offerData.generalData.discount = 0;
      return;
    }

    const sumOfExtras = extras?.reduce((i: number, { price }: any) => +i + +price, 0) || 0;

    this.offerData.generalData.discount = Number((rrp - offerPrice + sumOfExtras).toFixed(2));
  };

  public analyzeOfferFile = async (): Promise<void> => {
    const file = this.offerData.offerFile?.file;
    if (!file) return;

    this.analyzing = true;

    await this.offerModel.getRecognitionResult(this.offerData.type, file)
      .then((res) => {
        this.toastService.toast(ToastType.SUCCESS, 'Daten erfolgreich analysiert.');

        /** VAT changed */
        if (res.includesVat !== this.offerData.generalData.includesVat) {
          this.offerData.generalData.includesVat = res.includesVat;

          this.calculateVatFields(false, true);
        }

        switch (this.offerData.type) {
          case EOfferType.LEASING:
            this.offerData.leasingData = { ...this.offerData.leasingData, ...res };
            break;

          case EOfferType.FINANCE:
            this.offerData.financingData = { ...this.offerData.financingData, ...res };
            break;

          case EOfferType.RESIDUAL_LEASING:
            this.offerData.residualData = { ...this.offerData.residualData, ...res };
            break;

          default: break;
        }
      })
      .catch((e) => {
        this.logService.error(e);
        this.toastService.toast(ToastType.ERROR, 'Fehler beim Analysieren des Angebots.');
      });

    this.analyzing = false;
  };

  public exitForm = (): void => {
    this.exit({ removeLock: true });
  };

  private uploadFile = async (): Promise<null | string> => {
    if (!this.offerData.offerFile?.file) return null;

    const staticRequest: StaticUploadRequest = {
      identifier: 'TEMP',
      type: EStaticFileType.OFFER,
    };

    const staticId = await this.fileService
      .apiUploadStatic(this.offerData.offerFile.file, staticRequest, this.offerData.offerFile.progressBar);

    return staticId;
  };

  /** With the help of `OfferFormService` this method will return
   * an `OfferStub` that can be used to create a new offer. Depending
   * on the `EOfferType` different data is used and required. */
  public getOfferStub = async <T extends OfferData>(): Promise<OfferStub<T>> => {
    /** Either RRP or offerPrice must be defined. */
    if (!this.offerData.generalData.rrp && !this.offerData.generalData.offerPrice) {
      this.toastService.toast(ToastType.ERROR, 'Es muss entweder ein Angebotspreis oder die UPE des Fahrzeugs angegeben werden.');
      throw new Error('No offer price and no rrp defined');
    }

    const offerFile = await this.uploadFile();
    const generalStub = this.offerFormService.getGeneralOfferStub(
      this.offerData,
      offerFile,
      this.vehicle,
      this.$scope.dummyVehicle,
    );

    switch (this.offerData.type) {
      case EOfferType.LEASING:
        return this.offerFormService.getLeasingOfferStub(generalStub, this.offerData) as unknown as OfferStub<T>;

      case EOfferType.RESIDUAL_LEASING:
        return this.offerFormService.getResidualLeasingStub(generalStub, this.offerData) as unknown as OfferStub<T>;

      case EOfferType.FINANCE:
        return this.offerFormService.getLeasingOfferStub(generalStub, this.offerData) as unknown as OfferStub<T>;

      case EOfferType.CASH:
        return generalStub as OfferStub<T>;

      default:
        throw new Error('Offer type not defined');
    }
  };

  /** Retreives `TransferCosts` for the `AutoboardVehicle`s
   * `make` and/or `model` and uses the result to put the transfer
   * costs as an extra to the offer data. */
  public addTransferCosts = async (): Promise<void> => {
    const make = this.vehicle.modelData?.make || 'undefined';
    const model = this.vehicle.modelData?.model || 'undefined';

    const res = await this.salesApiService.getTransferCost(make, model);
    if (res.transferCosts) {
      if (this.offerData.generalData.includesVat) {
        res.transferCosts = this.numberConversionService.calculateVat(res.transferCosts, true);
      }

      this.offerData.generalData.extras?.push({ description: 'Überführungskosten', price: res.transferCosts });
      this.calculateDiscount();
    } else {
      this.toastService.toast(ToastType.WARNING, 'Keine Überführungskosten für diese Marke oder dieses Modell definiert.');
    }
  };

  public formTransmission = async (): Promise<void> => {
    try {
      this.sending = true;
      let offer: Offer<OfferData> | null = null;

      const offerStub = await this.getOfferStub();
      offer = await this.offerModel.postOffer(offerStub);

      this.toastService.toast(ToastType.SUCCESS, 'Angebot wurde erstellt');
      this.sending = false;

      const redirectUrl = `/opportunities/opportunity/${offer?.opportunityId}`;

      this.exit({ removeLock: true, redirect: redirectUrl });
    } catch (e) {
      console.error(e);
      this.toastService.toast(ToastType.ERROR, 'Fehler bei Erstellung des Angebots.');
      this.sending = false;
    }
  };
}
